import { ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { scoreLevel } from "./score-colors";

interface MetricScore {
  id: string;
  locationId: string;
  metricId: string;
  metricName: string;
  categoryId: string;
  score: string;
  isEstimated: boolean;
  rawValue: unknown;
  dataSource: string;
  scoredAt: string;
}

interface CategoryScore {
  categoryId: string;
  categoryName: string;
  score: number;
  metricsScored: number;
  metricsTotal: number;
}

export interface ScoreDetailSummary {
  metricsScored: number;
  metricsTotal: number;
  categoryCount: number;
}

interface ScoreDetailBodyProps {
  active: boolean;
  campaignId: string;
  locationId: string;
  className?: string;
  /** Reports rolled-up counts to the parent (for the header + breakdown eyebrow). */
  onSummary?: (summary: ScoreDetailSummary) => void;
}

const TONE = { high: "text-score-high", medium: "text-score-med", low: "text-score-low" } as const;
const BAR = { high: "bg-score-high", medium: "bg-score-med", low: "bg-score-low" } as const;

function scoreTone(score: number) {
  return TONE[scoreLevel(score)];
}

function scoreBar(score: number) {
  return BAR[scoreLevel(score)];
}

const SOURCE_ACRONYMS = new Set(["acs", "osm", "fema", "fbi", "epa", "hud", "irs", "bls"]);

/** Prettify a data-source slug, e.g. "census_acs" → "Census ACS", "google_places" → "Google Places". */
function prettySource(source: string): string {
  if (!source) return "—";
  return source
    .split(/[_\s]+/)
    .map((w) =>
      SOURCE_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");
}

export function ScoreDetailBody({
  active,
  campaignId,
  locationId,
  className,
  onSummary,
}: ScoreDetailBodyProps) {
  const [metrics, setMetrics] = useState<MetricScore[]>([]);
  const [categoryScores, setCategoryScores] = useState<CategoryScore[]>([]);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!active || !locationId) return;

    let mounted = true;
    setLoading(true);
    setError(false);
    setExpandedCategoryId(null);

    fetch(`/api/campaigns/${campaignId}/scores/${locationId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((json) => {
        if (!mounted) return;
        setMetrics(json.metrics ?? json.data ?? []);
        setCategoryScores(json.categoryScores ?? []);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [active, campaignId, locationId]);

  const metricsByCategory = useMemo(() => {
    const map = new Map<string, MetricScore[]>();
    for (const m of metrics) {
      const list = map.get(m.categoryId) ?? [];
      list.push(m);
      map.set(m.categoryId, list);
    }
    return map;
  }, [metrics]);

  // Roll up counts and report them to the parent (header + breakdown eyebrow).
  useEffect(() => {
    if (!onSummary || categoryScores.length === 0) return;
    let scored = 0;
    let total = 0;
    for (const cat of categoryScores) {
      scored += cat.metricsScored;
      total += cat.metricsTotal;
    }
    onSummary({ metricsScored: scored, metricsTotal: total, categoryCount: categoryScores.length });
  }, [categoryScores, onSummary]);

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-8", className)}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("py-8 text-center text-sm text-destructive", className)}>
        Failed to load score details.
      </div>
    );
  }

  if (categoryScores.length === 0) {
    return (
      <div className={cn("py-8 text-center text-sm text-muted-foreground", className)}>
        No score data available for this location.
      </div>
    );
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      {categoryScores.map((cat) => {
        const expanded = expandedCategoryId === cat.categoryId;
        const catMetrics = metricsByCategory.get(cat.categoryId) ?? [];
        const roundedScore = Math.round(cat.score);
        return (
          <div
            key={cat.categoryId}
            className={cn(
              "rounded-lg border transition-colors",
              expanded ? "border-primary/30 bg-muted/30" : "border-border",
            )}
          >
            <button
              type="button"
              onClick={() => setExpandedCategoryId(expanded ? null : cat.categoryId)}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors rounded-lg"
              aria-expanded={expanded}
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform shrink-0",
                  expanded && "rotate-90",
                )}
              />
              <span className="flex-1 text-sm font-medium truncate">{cat.categoryName}</span>
              <div className="flex items-center gap-2 shrink-0">
                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn("h-full rounded-full", scoreBar(roundedScore))}
                    style={{ width: `${Math.min(roundedScore, 100)}%` }}
                  />
                </div>
                <span
                  className={cn(
                    "w-9 text-right tabular-nums text-sm font-semibold",
                    scoreTone(roundedScore),
                  )}
                >
                  {roundedScore}
                </span>
                <span className="w-12 text-right tabular-nums text-[10px] text-muted-foreground">
                  {cat.metricsScored}/{cat.metricsTotal}
                </span>
              </div>
            </button>

            {expanded && catMetrics.length > 0 && (
              <div className="border-t border-border/60 px-3 py-2">
                <div className="flex flex-col">
                  {catMetrics.map((m) => {
                    const score = Math.round(Number(m.score));
                    // Estimated = the upstream value couldn't be fetched and fell back
                    // (see data-sources rule). Render honestly: no colored score bar.
                    const estimated = m.isEstimated;
                    return (
                      <div
                        key={m.id}
                        className="flex items-center gap-2 border-b border-border/40 py-1.5 last:border-0"
                      >
                        <span
                          className={cn(
                            "min-w-0 flex-1 truncate text-xs",
                            estimated ? "italic text-muted-foreground" : "text-foreground",
                          )}
                          title={m.metricName}
                        >
                          {m.metricName}
                        </span>

                        {estimated ? (
                          <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground/70">
                            No data
                          </span>
                        ) : (
                          <>
                            <span className="w-6 shrink-0 text-right font-mono text-xs tabular-nums">
                              {score}
                            </span>
                            <span className="h-1 w-10 shrink-0 overflow-hidden rounded-full bg-muted">
                              <span
                                className={cn("block h-full rounded-full", scoreBar(score))}
                                style={{ width: `${Math.min(score, 100)}%` }}
                              />
                            </span>
                            {m.dataSource && (
                              <span
                                className="max-w-[88px] shrink-0 truncate rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground"
                                title={prettySource(m.dataSource)}
                              >
                                {prettySource(m.dataSource)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {expanded && catMetrics.length === 0 && (
              <div className="border-t border-border/60 px-3 py-2 text-xs text-muted-foreground">
                No metric detail available.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
